import { UseFormReturn, useWatch } from "react-hook-form";
import { useEffect } from "react";

import { Chip, Skeleton } from "@chef/components";
import { UseExtendedFormReturn } from "@chef/hooks";
import { isDeepEquals } from "@chef/utils/equal";

import { RatingAndCommentFormValues } from "./RatingAndCommentContainer";
import { useQuikCommentsForSelectedRating } from "./useQuickCommentsForSelectedRating";

interface QuikCommentsProps {
  formControls: UseFormReturn<RatingAndCommentFormValues> &
    UseExtendedFormReturn<RatingAndCommentFormValues>;
  rating: number;
}

export const QuickComments = ({ formControls, rating }: QuikCommentsProps) => {
  const { setValue } = formControls;

  const {
    quickCommentsForRating,
    quickCommentIdsForSelectedRating,
    isLoadingRecipeQuickCommentsQuery,
  } = useQuikCommentsForSelectedRating({ rating });

  const watchedQuickCommentIds = useWatch({
    control: formControls.control,
    name: "quickCommentIds",
  });

  useEffect(() => {
    if (
      !rating ||
      !watchedQuickCommentIds ||
      !quickCommentIdsForSelectedRating
    ) {
      return;
    }

    const filteredQuickCommentIds = watchedQuickCommentIds.filter((id) =>
      quickCommentIdsForSelectedRating.includes(id),
    );

    const shouldUpdateQuickCommentIds = !isDeepEquals(
      watchedQuickCommentIds,
      filteredQuickCommentIds,
    );

    if (shouldUpdateQuickCommentIds) {
      setValue("quickCommentIds", filteredQuickCommentIds);
    }
  }, [
    watchedQuickCommentIds,
    quickCommentIdsForSelectedRating,
    rating,
    setValue,
  ]);

  if (isLoadingRecipeQuickCommentsQuery) {
    return <QuickCommentsSkeleton />;
  }

  const updateQuickCommentIds = (commentId: string) => {
    const isSelected = watchedQuickCommentIds?.includes(commentId);
    const currentQuickCommentIds = watchedQuickCommentIds ?? [];

    let updatedCommentIds = watchedQuickCommentIds;

    if (isSelected) {
      updatedCommentIds = currentQuickCommentIds?.filter(
        (id) => id !== commentId,
      );
    } else {
      updatedCommentIds = [...currentQuickCommentIds, commentId];
    }

    setValue("quickCommentIds", updatedCommentIds);
  };

  return (
    <div className="flex flex-wrap justify-center gap-4">
      {quickCommentsForRating?.map((comment) => {
        const isSelected = watchedQuickCommentIds?.includes(comment.id);

        return (
          <Chip
            onClick={() => updateQuickCommentIds(comment.id)}
            key={comment.id}
            selected={isSelected}
          >
            {comment.quickComment}
          </Chip>
        );
      })}
    </div>
  );
};

const QuickCommentsSkeleton = () => {
  return (
    <div className="flex flex-wrap justify-center gap-4">
      {[...Array(9)].map((_, i) => (
        <Skeleton width={40} height={8} key={i} />
      ))}
    </div>
  );
};
